/** DOM-free helpers for the arena bake (15 §2.1): fast inverse projection of
 *  the floor plane, seeded value noise, floodlight falloff, pour seams, void
 *  detection. Unit-tested; `arena-bake.ts` only adds the canvas plumbing. */
import { VIRTUAL_H, VIRTUAL_W } from '../../../sim/constants.js'
import { gapOf, isOnPad } from '../../../sim/arena.js'
import type { Arena, Team, Vec3 } from '../../../sim/types.js'
import { DEFAULT_CAMERA, focalLength, toTeamLocal, type CameraParams } from '../camera.js'
import { hash2 } from './rng.js'

/** Precomputed camera terms for a per-pixel inverse of `project()` on plane y. */
export interface FloorInverse { (sx: number, sy: number): { x: number; z: number; zc: number } | null }

export function floorInverse(team: Team, arena: Arena, planeY = 0, cam: CameraParams = DEFAULT_CAMERA): FloorInverse {
  const f = focalLength(cam)
  const pitch = (cam.pitchDeg * Math.PI) / 180
  const cp = Math.cos(pitch), sp = Math.sin(pitch)
  const oy = cam.height, oz = -cam.back
  return (sx, sy) => {
    const xc = (sx - VIRTUAL_W / 2) / f
    const yc = (VIRTUAL_H / 2 - sy) / f
    const dy = yc * cp - sp
    const dz = yc * sp + cp
    if (Math.abs(dy) < 1e-9) return null
    const t = (planeY - oy) / dy
    if (t <= 0) return null
    const lx = xc * t, lz = oz + dz * t
    const zc = (planeY - oy) * -sp + (lz + cam.back) * cp
    if (zc < cam.near) return null
    const w = toTeamLocal(lx, lz, team, arena.length)
    return { x: w.lx, z: w.lz, zc }
  }
}

/** Smooth value noise in [0,1) on a 2-D lattice. */
export function valueNoise(x: number, y: number, seed = 0): number {
  const ix = Math.floor(x), iy = Math.floor(y)
  const fx = x - ix, fy = y - iy
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy)
  const v = (a: number, b: number) => hash2(a, b, seed) / 4294967296
  const a = v(ix, iy), b = v(ix + 1, iy), c = v(ix, iy + 1), d = v(ix + 1, iy + 1)
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy
}

/** Concrete value in [0,1): three noise octaves plus a per-2 m-tile pour offset (±0.06) so slabs differ. */
export function concreteValue(x: number, z: number, seed = 7): number {
  const n = 0.5 * valueNoise(x * 0.9, z * 0.9, seed) + 0.3 * valueNoise(x * 3.7, z * 3.7, seed + 1) + 0.2 * valueNoise(x * 13, z * 13, seed + 2)
  const pour = (hash2(Math.floor(x / 2), Math.floor(z / 2), seed + 3) / 4294967296 - 0.5) * 0.12
  return Math.max(0, Math.min(0.9999, n + pour))
}

/** 15 §2.1: four floodlights at (±4, 6, 7.5) and (±4, 6, 22.5); one hard step. corridor_a's set — `lightsFor(arena)` for the rest. */
export const FLOODLIGHTS: readonly Vec3[] = [
  { x: -4, y: 6, z: 7.5 }, { x: 4, y: 6, z: 7.5 }, { x: -4, y: 6, z: 22.5 }, { x: 4, y: 6, z: 22.5 },
]
/** S10 (spec §3): the arena's floodlights (`arena.lights`, render-only data carried with the arena) or corridor_a's. */
export function lightsFor(arena: Arena): readonly Vec3[] { return arena.lights ?? FLOODLIGHTS }

/** Screen-x side the light comes from for an athlete/disc at world `x` under the `cam` team camera:
 *  the nearer floodlight column (x = ±4) lights it, so the split flips at the corridor centre line.
 *  −1 = light from screen-left. */
export function lightAzimuthSide(worldX: number, cam: Team): -1 | 1 {
  const world: -1 | 1 = worldX < 0 ? -1 : 1
  return cam === 0 ? world : (world === -1 ? 1 : -1)
}

/** Sum over the lights of I / (1 + d²/25) (inverse square with a floor) times a soft cone cos²θ, each
 *  light aiming down and inward at the corridor centre line. Unnormalised. */
function floodRaw(x: number, z: number, y: number, lights: readonly Vec3[]): number {
  let s = 0
  for (const l of lights) {
    const dx = x - l.x, dy = y - l.y, dz = z - l.z
    const d2 = dx * dx + dy * dy + dz * dz
    const d = Math.sqrt(d2) || 1e-9
    // Aim vector (−l.x, −l.y, 0): from the light to the floor point beneath the corridor centre.
    const al = Math.hypot(l.x, l.y) || 1
    const cos = (dx * -l.x + dy * -l.y) / (d * al)
    const c = Math.max(0, cos)
    s += (c * c) / (1 + d2 / 25)
  }
  return s
}
/** Per light-set reference: the floor point on the centre line midway between the light rows ((0, 15) for corridor_a). */
const floodRefs = new WeakMap<readonly Vec3[], number>()
function floodRef(lights: readonly Vec3[]): number {
  let r = floodRefs.get(lights)
  if (r === undefined) { const zc = lights.length ? lights.reduce((a, l) => a + l.z, 0) / lights.length : 15; r = floodRaw(0, zc, 0, lights) || 1; floodRefs.set(lights, r) }
  return r
}
/** Floodlight intensity in [0, 1.4], normalised so the corridor centre reads 1.0 under the given lights (default corridor_a's). */
export function floodlight(x: number, z: number, y = 0, lights: readonly Vec3[] = FLOODLIGHTS): number {
  return Math.min(1.4, floodRaw(x, z, y, lights) / floodRef(lights))
}

/** Distance to the nearest 2 m pour seam (with seeded jitter) in metres. */
export function seamDistance(x: number, z: number, seed = 3): number {
  const jx = (hash2(Math.round(x / 2), 0, seed) / 4294967296 - 0.5) * 0.06
  const jz = (hash2(0, Math.round(z / 2), seed) / 4294967296 - 0.5) * 0.06
  const dx = Math.abs(((x + 1 + jx) % 2 + 2) % 2 - 1)
  const dz = Math.abs(((z + 1 + jz) % 2 + 2) % 2 - 1)
  return Math.min(dx, dz)
}

/** 02 §2: anything not over a pad is void (the gap, in the corridors). */
export function inVoid(arena: Arena, x: number, z: number): boolean {
  return !isOnPad(arena, x, z)
}

/** Bake annotation labels (15 §2.1 dimension lines) from the arena: zone depth, width and the gap (`gapOf`, sim/arena.ts). */
export function annotationLabels(arena: Arena): { zoneDepth: string; width: string; gap: string } {
  const z0 = arena.zones.find((z) => z.team === 0)
  const depth = z0 ? z0.zMax - z0.zMin : arena.length / 3
  const g = gapOf(arena)
  return { zoneDepth: depth.toFixed(2), width: arena.width.toFixed(2), gap: `GAP ${(g.end - g.start).toFixed(2)}` }
}

export function onFloor(arena: Arena, x: number, z: number): boolean {
  const hw = arena.width / 2
  return x >= -hw && x <= hw && z >= 0 && z <= arena.length
}

const smoothstep01 = (t: number): number => { const c = t <= 0 ? 0 : t >= 1 ? 1 : t; return c * c * (3 - 2 * c) }
/** Ambient occlusion factor in [0.55, 1] (1 = none): smoothstep over 0.6 m from walls and pillars. */
export function ambientOcclusion(arena: Arena, x: number, z: number): number {
  const hw = arena.width / 2
  const dw = Math.min(hw - Math.abs(x), Math.min(z, arena.length - z))
  let ao = 1 - 0.45 * (1 - smoothstep01(dw / 0.6))
  for (const p of arena.pillars) { const d = Math.hypot(x - p.x, z - p.z) - p.r; if (d < 0.6) ao *= 1 - 0.45 * (1 - smoothstep01(d / 0.6)) }
  return Math.max(0.55, ao)
}

